import {Injectable} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {FuseConfirmDialogComponent} from 'fuse-core/components/confirm-dialog/confirm-dialog.component';

/**
 * permets de naviguer entre menu et différent contenu principal avec ALT + SHIFT + KEY
 * on peut passer de card en card avec ALT +shift + A et ALT +SHIFT + Z
 * contenu central ALT + SHIFT + M
 * menu ALT + SHIFT + L
 * dernier element du menu ALT + SHIFT + O
 * ALT + shift + W ALT + shift +X focus sur les paginator des tableau ou dans le header des exo
 * ALT + shift + U focus sur le menu user
 * ALT + shift + H bouton help
 * ALT + shift + F filter button if exist
 * ALT + shift + D details on corpus (mediateque)
 */
@Injectable({
    providedIn: 'root'
})
export class AccessibilityService {
    private lastFocusPath: any;

    constructor(
        private dialog: MatDialog,
        private translate: TranslateService,
    ) {
    }

    init() {
        this.info();
        this.storeLastMenuElementFocused();
        this.setAccessibilityEvent();
        this.accessibilityCookiesFirstIfExist();
        this.accessibilityCookiesGoToSkipContentAfterCookies();
        this.initSkipLinkAfterCookies();
    }

    /**
     * will listen form shift + some key to help focus accessibility
     * @private
     */
    private setAccessibilityEvent(): void {
        document.addEventListener('keydown', (event: any) => {
            const isTypingZone =
                ['INPUT', 'TEXTAREA'].includes(event.target.tagName) || event.target.isContentEditable;
            if (isTypingZone) {
                return;
            }

            this.mainMenuFocus(event);
            this.mainCentralBlocFocus(event);
            this.previousMenuElementFocus(event);
            this.cardJumping(event);
            this.arrayNavigationFocusShortcut(event);
            this.userMenu(event);
            this.helpButton(event);
            this.filterButton(event);
            this.leftDetailsButton(event);
        });
    }

    /**
     * shift + L focus sur le menu tout en haut
     */
    private mainMenuFocus(event: any): void {
        if (event.shiftKey && event.altKey && event.key === 'L') {
            var menu = document.querySelector('.navbar-header') as any;
            if (menu) {
                // standart menu
                menu.focus();
            } else {
                // ILE menu
                var menu = document.querySelector('.pretty-easy-navigation__header') as any;
                if (menu) {
                    menu.focus();
                }
            }
        }
    }

    /**
     * focus sur le dernier élément du menu ou le focus était sur le menu
     * @param event
     * @private
     */
    private previousMenuElementFocus(event: any) {
        //
        if (event.shiftKey && event.altKey && event.key === 'O') {
            if (this.lastFocusPath) {
                this.lastFocusPath.focus();
            }
        }
    }

    /**
     * shift + M contenu central
     * @private
     */
    private mainCentralBlocFocus(event: any): void {
        if (event.shiftKey && event.altKey && event.key === 'M') {
            var maincontent = document.querySelector('#maincontent') as any;
            if (maincontent) {
                maincontent.focus();
            }
        }
    }

    private storeLastMenuElementFocused(): void {
        document.addEventListener('focusin', (e) => {
            const menuItem = (<any>e.target).closest('#mainmenuelem');
            if (menuItem) {
                this.lastFocusPath = <HTMLElement>e.target;
            }
        });
    }

    /*
     * shift + A et shift +Z pour sauter de card en card marche pour mat-card et app-card
     */
    public cardJumping(event: any): void {
        let comeFromMainContent = false;
        let cards = [];
        let target = 'app-card';
        if (event.shiftKey && event.altKey && (event.key === 'A' || event.key === 'Z')) {
            cards = Array.from(document.querySelectorAll('app-card'));
            if (cards.length === 0) {
                cards = Array.from(document.querySelectorAll('mat-card'));
                target = 'mat-card';
            }
            if (cards.length === 0) {
                return;
            }
        }
        //previous card
        if (event.shiftKey && event.altKey && event.key === 'A') {
            const activeElement = document.activeElement as HTMLElement;
            const currentCard = activeElement.closest(target);
            const currentIndex = cards.indexOf(currentCard as Element);

            if (currentIndex === -1) {
                return;
            }

            event.preventDefault();
            let nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
            if (nextIndex < 0) {
                nextIndex = cards.length - 1;
            }
            if (nextIndex >= cards.length) {
                nextIndex = 0;
            }

            const nextCard = cards[nextIndex];
            this.focusFirstFocusable(nextCard);
        }
        // next card
        else if (event.shiftKey && event.altKey && event.key === 'Z') {
            const activeElement = document.activeElement as HTMLElement;
            let currentCard = activeElement.closest(target);
            if (!currentCard) {
                // we come from main content there s no close card we take the first one
                currentCard = cards[0];
                comeFromMainContent = true;
            }
            const currentIndex = cards.indexOf(currentCard as Element);

            if (currentIndex === -1) {
                return;
            }
            event.preventDefault();

            let nextIndex = currentIndex + 1;
            // if we come from main content we take the first one not the next one
            if (nextIndex >= cards.length || comeFromMainContent) {
                nextIndex = 0;
            }

            const nextCard = cards[nextIndex];
            this.focusFirstFocusable(nextCard);
        }
    }

    /**
     * focus sur le premier élément focusable dans une carte
     */
    private focusFirstFocusable(card: Element) {
        const focusableSelectors = 'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])';
        const focusables = Array.from(card.querySelectorAll(focusableSelectors)) as HTMLElement[];
        const first = focusables.find(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
        if (first) {
            first.focus();
        }
    }

    /**
     * focus sur le dernier élément focusable
     */
    private focusLastFocusable(card: Element) {
        const focusableSelectors = 'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])';
        const focusables = Array.from(card.querySelectorAll(focusableSelectors)) as HTMLElement[];
        const last = focusables.reverse().find(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
        if (last) {
            last.focus();
        }
    }

    /*
     * if table array exist focus on next or previous shortcut table and header of exo
     */
    arrayNavigationFocusShortcut(event: any): void {
        if (event.shiftKey && event.altKey && event.key === 'W') {
            // focus on previous button if exist on table or on lesson header
            const selectorsPrevious = [
                '.mat-mdc-paginator-navigation-previous',
                '.generic-stepper__previous-button',
            ];

            let filterButton: HTMLElement | null = null;
            for (const selector of selectorsPrevious) {
                const button = document.querySelector(selector) as HTMLElement | null;
                if (button) {
                    filterButton = button;
                    break;
                }
            }

            if (filterButton) {
                filterButton.focus();
            }
        }

        // focus on next button if exist
        if (event.shiftKey && event.altKey && event.key === 'X') {
            const selectorsPrevious = [
                '.generic-stepper__next-button',
                '.mat-mdc-paginator-navigation-next',
            ];

            let filterButton: HTMLElement | null = null;
            for (const selector of selectorsPrevious) {
                const button = document.querySelector(selector) as HTMLElement | null;
                if (button) {
                    filterButton = button;
                    break;
                }
            }

            if (filterButton) {
                filterButton.focus();
            }
        }
    }

    /**
     * focus on user menu
     */
    private userMenu(event: any): void {
        if (event.shiftKey && event.altKey && event.key === 'U') {
            // focus on user menu
            const userMenuButton = <any>document.querySelector('.user-button');
            if (userMenuButton) {
                userMenuButton.focus();
            }
        }
    }

    /**
     * focus on help button
     */
    private helpButton(event: any): void {
        if (event.shiftKey && event.altKey && event.key === 'H') {
            // focus on Help button
            const helpButton = <any>document.querySelector('app-button-help button');
            if (helpButton) {
                helpButton.focus();
            }
        }
    }

    /**
     * focus on details zone
     */
    private leftDetailsButton(event: any): void {
        if (event.shiftKey && event.altKey && event.key === 'D') {
            // focus on Help button
            const details = <any>document.querySelector('fuse-corpus-details-sidenav');
            if (details) {
                details.focus();
            }
        }
    }

    /**
     * focus on filter button
     */
    private filterButton(event: KeyboardEvent): void {
        if (event.shiftKey && event.altKey && (event.key === 'F' || event.key === 'f')) {
            const selectors = [
                '.followed-list__filter-button',
                '.corpus-file-list__filter-button',
                '.lessons-tab__filter-button'
            ];

            let filterButton: HTMLElement | null = null;
            for (const selector of selectors) {
                const button = document.querySelector(selector) as HTMLElement | null;
                if (button) {
                    filterButton = button;
                    break;
                }
            }

            if (filterButton) {
                filterButton.focus();
            }
        }
    }

    /**
     * if cookies exist focus to accept first
     * @private
     */
    private accessibilityCookiesFirstIfExist(): void {
        let cookieFocusDone = false;
        document.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || cookieFocusDone) {
                return;
            }

            const cookieWindow = document.getElementById('tarteaucitronAlertBig');
            const acceptButton = <any>document.querySelector<HTMLButtonElement>(
                '.tarteaucitronCTAButton.tarteaucitronAllow'
            );

            if (
                cookieWindow &&
                cookieWindow.style.display !== 'none' &&
                acceptButton
            ) {
                event.preventDefault();
                acceptButton.focus();
                cookieFocusDone = true;
            }
        });
    }

    /**
     * if user is on cookies at last button on tab go to skip content to go away cookies zone
     * @private
     */
    private accessibilityCookiesGoToSkipContentAfterCookies(): void {
        document.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || event.shiftKey) {
                return;
            }

            const personalizeButton = document.getElementById('tarteaucitronCloseAlert');
            const skipToContentDiv = document.getElementById('skiptocontent');
            const skipLink = skipToContentDiv?.querySelector<HTMLAnchorElement>('a');

            if (!personalizeButton || !skipLink) {
                return;
            }

            if (event.target === personalizeButton) {
                event.preventDefault();
                skipToContentDiv.style.visibility = 'visible';
                skipLink.focus();
            }
        });
    }

    /**
     * after accept or refused cookies the next tab must open skip link navigation
     * @private
     */
    private initSkipLinkAfterCookies(): void {
        let cookiesHandled = false;
        let firstTabAfterCookies = false;
        document.addEventListener('click', (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            if (
                target?.classList.contains('tarteaucitronAllow') ||
                target?.classList.contains('tarteaucitronDeny')
            ) {
                cookiesHandled = true;
                firstTabAfterCookies = true;
            }
        });

        document.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Tab' && cookiesHandled && firstTabAfterCookies) {
                const skipToContentDiv = document.getElementById('skiptocontent');
                const skipLink = skipToContentDiv?.querySelector<HTMLAnchorElement>('a');

                if (skipToContentDiv && skipLink) {
                    console.log('focus');
                    event.preventDefault();
                    skipToContentDiv.style.visibility = 'visible';
                    skipLink.focus();
                    firstTabAfterCookies = false;
                }
            }
        });
    }

    private info(): void {
        // remove true when modal was validate
        if (true || localStorage.getItem('popupAccessibilityTriggered')) {
            console.log('Popup déjà déclenchée');
        } else {
            let tabPressCount = 0;
            document.addEventListener('keydown', (event) => {
                if (event.key === 'Tab') {
                    tabPressCount = tabPressCount + 1;
                    if (tabPressCount === 10 && !localStorage.getItem('popupAccessibilityTriggered')) {
                        const dialogConfig = new MatDialogConfig();
                        dialogConfig.data = {
                            titleDialog: 'accessibility.title',
                            bodyDialog: 'accessibility.modal_content',
                        };
                        this.translate.get(dialogConfig.data.titleDialog).subscribe((translation: string) => dialogConfig.data.titleDialog = translation);
                        this.translate.get(dialogConfig.data.bodyDialog).subscribe((translation: string) => dialogConfig.data.bodyDialog = translation);
                        dialogConfig.backdropClass = 'backdrop-blur';
                        const dialogRef = this.dialog.open(FuseConfirmDialogComponent, dialogConfig);

                        localStorage.setItem('popupAccessibilityTriggered', 'true');
                    }
                }
            });
        }
    }
}